import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import EditIcon from '@atlaskit/icon/core/edit';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { Conversation } from '../../types';
import { filterAndSortConversations } from '../../utils/conversation';
import { DeleteConversationModal } from '../chat-conversation-list/conversation-modal/delete-conversation-modal';
import { EditConversationModal } from '../chat-conversation-list/conversation-modal/edit-conversation-modal';
import { ConversationPaginationControls } from '../chat-conversation-list/conversation-pagination-controls';
import { useConversationModal } from '../hooks/conversation-modal';
import { SearchInput } from '../search-input';

import { ChatItem } from './chat-item';
import messages from './messages';

const fsStyles = cssMap({
	wrapper: {
		overflowX: 'hidden',
		overflowY: 'hidden',
		height: '100%',
		width: '100%',
		paddingLeft: token('space.400'),
		paddingRight: token('space.400'),
	},

	redesignWrapper: {
		overflowX: 'hidden',
		overflowY: 'hidden',
		height: '100%',
		width: '100%',
		paddingLeft: token('space.0'),
		paddingRight: token('space.0'),
	},

	contentContainer: {
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
		minHeight: '0px',
		height: '100%',
		width: '100%',
		maxWidth: '800px',
		marginInlineStart: 'auto',
		marginInlineEnd: 'auto',
		paddingTop: token('space.500'),
		paddingBottom: token('space.400'),
	},

	legacyContentContainer: {
		display: 'flex',
		flexDirection: 'column',
		minHeight: '0px',
		height: '100%',
		width: '100%',
	},

	headerContainer: {
		gap: token('space.200'),
		paddingTop: token('space.250'),
		paddingBottom: token('space.250'),
	},

	redesignHeaderContainer: {
		paddingTop: token('space.0'),
		paddingBottom: token('space.0'),
	},

	header: {
		justifyContent: 'space-between',
	},

	searchBarContainer: {
		display: 'flex',
		marginTop: token('space.200'),
	},
	conversationsContainer: {
		gap: token('space.100'),
		overflowY: 'auto',
		flex: '1',
		minHeight: '0px',
	},

	redesignConversationsContainer: {
		display: 'flex',
		flexDirection: 'column',
		flex: '1',
		minHeight: '0px',
		marginTop: token('space.300'),
		overflowX: 'hidden',
		overflowY: 'hidden',
		backgroundColor: token('elevation.surface'),
	},

	redesignSearchResultsContainer: {
		display: 'flex',
		flexDirection: 'column',
		flex: 'initial',
		minHeight: 'auto',
		marginTop: token('space.300'),
		overflowX: 'hidden',
		overflowY: 'hidden',
		backgroundColor: token('elevation.surface'),
	},

	redesignConversationList: {
		overflowX: 'hidden',
		overflowY: 'auto',
		flex: '1',
		minHeight: '0px',
		listStyle: 'none',
		marginBlockStart: token('space.0'),
		marginBlockEnd: token('space.0'),
		paddingInlineStart: token('space.0'),
	},

	conversationRow: {
		marginBlockStart: token('space.0'),
		backgroundColor: token('elevation.surface'),
		listStyle: 'none',
	},
});

const styles = cssMap({
	wrapper: {
		overflowX: 'hidden',
		height: '100%',
		width: '100%',
	},
	recentChat: {
		overflow: 'hidden',
	},
	conversationList: {
		overflowY: 'auto',
		paddingInline: token('space.150'),
		listStyle: 'none',
		marginTop: token('space.0'),
	},
	searchBar: {
		position: 'sticky',
		paddingTop: token('space.100'),
		paddingBottom: token('space.200'),
		paddingInline: token('space.150'),
	},
	accessibleSearchResults: {
		opacity: 0,
		position: 'absolute',
	},
	chatItem: {
		marginTop: token('space.0'),
		listStyle: 'none',
	},
});

export type ChatConversationListRefreshProps = {
	conversations: Conversation[];

	searchQuery: string;

	title: string;

	searchPlaceholderText: string;

	onNewChatClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;

	onSearchQueryChange: (query: string) => void;

	onConversationSelect: (conversationId: string) => void;

	onConversationEdit: (conversationId: string, name: string) => Promise<void | { error: string }>;

	onConversationDelete: (conversationId: string) => Promise<void | { error: string }>;

	onConversationPinToggle?: (conversationId: string, isPinned: boolean) => void | Promise<void>;

	isSpinnerOverlayConversationIconEnabled: boolean;

	isFullScreen: boolean;

	/** replaces the current history panel header with this component */
	chatRefreshHeader?: React.ReactNode;

	/** Cloud / site id for scope file preview requests (`x-cloudid`). */
	cloudId?: string;

	hasMoreConversations?: boolean;
	isLoadingMoreConversations?: boolean;
	hasLoadMoreConversationsError?: boolean;
	onLoadMoreConversations?: () => void;
};

export const ChatConversationListRefresh = ({
	conversations,
	searchQuery,
	title,
	searchPlaceholderText,
	onNewChatClick,
	onSearchQueryChange,
	onConversationSelect,
	onConversationDelete,
	onConversationEdit,
	onConversationPinToggle,
	isSpinnerOverlayConversationIconEnabled,
	isFullScreen,
	chatRefreshHeader,
	cloudId,
	hasMoreConversations,
	isLoadingMoreConversations,
	hasLoadMoreConversationsError,
	onLoadMoreConversations,
}: ChatConversationListRefreshProps): React.JSX.Element => {
	// conversation to edit / delete.
	const { conversation } = useConversationModal();
	const { formatMessage } = useIntl();
	const isPinnedChatsEnabled = fg('rovo_chat_pinned_fe');
	const isAllChatsPageRedesignEnabled = fg('rovo_chat_redesign_all_chats_page');
	const gatedConversationPinToggle = isPinnedChatsEnabled ? onConversationPinToggle : undefined;

	const sortedConversations = useMemo(
		() =>
			filterAndSortConversations(conversations, {
				direction: 'desc',
				searchValue: searchQuery,
				sortPinned: isPinnedChatsEnabled,
			}),
		[conversations, isPinnedChatsEnabled, searchQuery],
	);
	const isSearchActive = searchQuery.trim().length > 0;
	const searchResultsAnnouncement = isSearchActive
		? formatMessage(messages.accessibleSearchResults, {
				results: sortedConversations.length,
			})
		: null;

	return isFullScreen ? (
		<Stack
			xcss={cx(
				fsStyles.wrapper,
				isAllChatsPageRedesignEnabled ? fsStyles.redesignWrapper : undefined,
			)}
			testId="ai-mate-conversation-list-refresh-view"
			alignBlock="start"
		>
			<Box
				xcss={
					isAllChatsPageRedesignEnabled
						? fsStyles.contentContainer
						: fsStyles.legacyContentContainer
				}
			>
				<Box
					xcss={cx(
						fsStyles.headerContainer,
						isAllChatsPageRedesignEnabled ? fsStyles.redesignHeaderContainer : undefined,
					)}
				>
					<Inline xcss={fsStyles.header}>
						<Heading size="large">{title}</Heading>
						<Button
							iconBefore={EditIcon}
							appearance="default"
							spacing="default"
							onClick={onNewChatClick}
						>
							{formatMessage(messages.newChatButtonLabel)}
						</Button>
					</Inline>

					<Box xcss={fsStyles.searchBarContainer}>
						<SearchInput
							labelText={searchPlaceholderText}
							placeholderText={isAllChatsPageRedesignEnabled ? undefined : searchPlaceholderText}
							onSearch={onSearchQueryChange}
							isCompact={isAllChatsPageRedesignEnabled}
						/>
					</Box>
				</Box>

				{isSearchActive && (
					<Box xcss={styles.accessibleSearchResults} role="status" aria-atomic>
						{searchResultsAnnouncement}
					</Box>
				)}

				<Box
					xcss={
						isAllChatsPageRedesignEnabled
							? isSearchActive
								? fsStyles.redesignSearchResultsContainer
								: fsStyles.redesignConversationsContainer
							: fsStyles.conversationsContainer
					}
					role={isAllChatsPageRedesignEnabled ? undefined : 'list'}
				>
					{isAllChatsPageRedesignEnabled ? (
						<Box xcss={fsStyles.redesignConversationList} as="ul">
							{sortedConversations.length === 0 && (
								<Box paddingBlockStart="space.200" as="li" xcss={styles.chatItem}>
									<Text align="center" color="color.text.subtle">
										{formatMessage(messages.noResults)}
									</Text>
								</Box>
							)}
							{sortedConversations.map((conversation) => (
								<Box key={conversation.id} as="li" xcss={fsStyles.conversationRow}>
									<ChatItem
										conversation={conversation}
										onConversationSelect={onConversationSelect}
										onConversationPinToggle={gatedConversationPinToggle}
										isPinnedChatsEnabled={isPinnedChatsEnabled}
										isSpinnerOverlayConversationIconEnabled={
											isSpinnerOverlayConversationIconEnabled
										}
										cloudId={cloudId}
									/>
								</Box>
							))}
							{!isSearchActive && (
								<Box as="li" xcss={fsStyles.conversationRow}>
									<ConversationPaginationControls
										as="div"
										hasMoreConversations={hasMoreConversations}
										isLoadingMoreConversations={isLoadingMoreConversations}
										hasLoadMoreConversationsError={hasLoadMoreConversationsError}
										onLoadMoreConversations={onLoadMoreConversations}
										testIdPrefix="chat-conversation-list-refresh"
									/>
								</Box>
							)}
						</Box>
					) : (
						<>
							{sortedConversations.map((conversation) => (
								<Box key={conversation.id} role="listitem">
									<ChatItem
										conversation={conversation}
										onConversationSelect={onConversationSelect}
										onConversationPinToggle={gatedConversationPinToggle}
										isPinnedChatsEnabled={isPinnedChatsEnabled}
										isSpinnerOverlayConversationIconEnabled={
											isSpinnerOverlayConversationIconEnabled
										}
										cloudId={cloudId}
									/>
								</Box>
							))}
							{!isSearchActive && (
								<ConversationPaginationControls
									as="div"
									role="listitem"
									hasMoreConversations={hasMoreConversations}
									isLoadingMoreConversations={isLoadingMoreConversations}
									hasLoadMoreConversationsError={hasLoadMoreConversationsError}
									onLoadMoreConversations={onLoadMoreConversations}
									testIdPrefix="chat-conversation-list-refresh"
								/>
							)}
						</>
					)}
				</Box>
			</Box>

			<EditConversationModal onEditConfirm={(name) => onConversationEdit(conversation.id, name)} />

			<DeleteConversationModal onDeleteConfirm={() => onConversationDelete(conversation.id)} />
		</Stack>
	) : (
		<Stack xcss={styles.wrapper} testId="ai-mate-conversation-list-view" alignBlock="start">
			{chatRefreshHeader && chatRefreshHeader}

			<Box xcss={styles.searchBar}>
				<SearchInput placeholderText={searchPlaceholderText} onSearch={onSearchQueryChange} />
			</Box>

			<Stack space="space.150" xcss={styles.recentChat}>
				{/* For screen readers to announce updated search results */}
				{isSearchActive && (
					<Box xcss={styles.accessibleSearchResults} role="status" aria-atomic>
						{searchResultsAnnouncement}
					</Box>
				)}
				<Stack xcss={styles.conversationList} as="ul">
					{sortedConversations.length === 0 && (
						<Box paddingBlockStart="space.200" as="li" xcss={styles.chatItem}>
							<Text align="center" color="color.text.subtle">
								{formatMessage(messages.noResults)}
							</Text>
						</Box>
					)}

					{sortedConversations.length > 0 &&
						sortedConversations.map((conversation) => (
							<Box key={conversation.id} as="li" xcss={styles.chatItem}>
								<ChatItem
									conversation={conversation}
									onConversationSelect={onConversationSelect}
									onConversationPinToggle={gatedConversationPinToggle}
									isPinnedChatsEnabled={isPinnedChatsEnabled}
									isSpinnerOverlayConversationIconEnabled={isSpinnerOverlayConversationIconEnabled}
									cloudId={cloudId}
								/>
							</Box>
						))}
					{!isSearchActive && (
						<ConversationPaginationControls
							as="li"
							hasMoreConversations={hasMoreConversations}
							isLoadingMoreConversations={isLoadingMoreConversations}
							hasLoadMoreConversationsError={hasLoadMoreConversationsError}
							onLoadMoreConversations={onLoadMoreConversations}
							testIdPrefix="chat-conversation-list-refresh"
						/>
					)}
				</Stack>
			</Stack>

			<EditConversationModal onEditConfirm={(name) => onConversationEdit(conversation.id, name)} />

			<DeleteConversationModal onDeleteConfirm={() => onConversationDelete(conversation.id)} />
		</Stack>
	);
};
